import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Button, Grid, Card, CardContent, Chip, IconButton,
  Tooltip, Divider, CircularProgress, Dialog, DialogTitle, DialogContent
} from '@mui/material';
import {
  Add, Edit, Delete, Visibility, Share, Analytics, AutoAwesome, Close,
  ContentCopy, Send
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { HEADERS } from '../../../../API/UrlAndPaths';
import FormBuilder, { FORM_TYPES } from './FormBuilder';
import FormPreview from './FormPreview';
import FormResponses from './FormResponses';
import SendFormDialog from './SendFormDialog';

const DEFAULT_FORM = {
  title: 'Untitled Form',
  description: '',
  type: 'SURVEY',
  themeColor: '#673ab7',
  fields: [],
  settings: {
    collectEmail: false,
    showProgressBar: true,
    confirmationMessage: 'Thank you for your response!',
    isActive: true
  }
};

const FormsManagement = () => {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [responsesOpen, setResponsesOpen] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [responses, setResponses] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);

  useEffect(() => {
    fetchForms();
  }, []);

  const fetchForms = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/crm-forms/templates', HEADERS);
      if (res.status === 200 || res.data.status === 200) {
        setForms(res.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching forms:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchResponses = async (formId) => {
    try {
      const res = await axios.get(`/api/crm-forms/submissions/${formId}`, HEADERS);
      if (res.status === 200 || res.data.status === 200) {
        setResponses(res.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching responses:', error);
    }
  };

  const generateFormWithAI = async () => {
    const { value: prompt } = await Swal.fire({
      title: 'Generate Form with AI',
      input: 'textarea',
      inputPlaceholder: 'Describe the form you want to create...\n\nExample: Customer satisfaction survey for a restaurant with questions about food quality, service speed, ambiance, and likelihood to recommend',
      inputAttributes: { style: 'height: 120px' },
      showCancelButton: true,
      confirmButtonText: 'Generate',
      confirmButtonColor: '#673ab7',
    });

    if (!prompt) return;

    setAiLoading(true);
    try {
      const res = await axios.post('/api/crm-forms/ai/generate', { prompt }, HEADERS);
      console.log('AI Generate Response:', res.data);
      
      // Check for success - backend returns status: 200 (STATUSCODES.SUCCESS)
      const isSuccess = res.status === 200 || res.data.status === 200 || res.data.status === 1;
      const aiForm = res.data.data;
      
      if (isSuccess && aiForm) {
        console.log('AI Form Data:', aiForm);
        console.log('AI Form Fields:', aiForm.fields);
        
        // Ensure fields have proper structure
        const processedFields = (aiForm.fields || []).map((field, idx) => ({
          id: field.id || `field_${Date.now()}_${idx}`,
          type: field.type || 'SHORT_TEXT',
          label: field.label || field.question || 'Untitled Question',
          description: field.description || field.helpText || '',
          required: field.required || false,
          options: field.options || [],
          settings: field.settings || { scaleMin: 1, scaleMax: 5 }
        }));
        
        console.log('Processed Fields:', processedFields);
        
        setFormData({
          ...DEFAULT_FORM,
          title: aiForm.title || 'AI Generated Form',
          description: aiForm.description || '',
          type: aiForm.type || 'SURVEY',
          fields: processedFields,
        });
        setSelectedForm(null);
        setBuilderOpen(true);
        Swal.fire('Success', `Form generated with ${processedFields.length} questions! Customize as needed.`, 'success');
      } else {
        console.error('AI response invalid:', res.data);
        Swal.fire('Error', res.data?.data || 'Failed to parse AI response', 'error');
      }
    } catch (error) {
      console.error('AI Generate Error:', error);
      Swal.fire('Error', error.response?.data?.data || 'Failed to generate. Configure AI API key in Notification Settings → AI tab.', 'error');
    } finally {
      setAiLoading(false);
    }
  };

  const improveQuestionWithAI = async (index) => {
    const field = formData.fields[index];
    setAiLoading(true);
    try {
      const res = await axios.post('/api/crm-forms/ai/improve-question', {
        question: field.label,
        type: field.type,
        options: field.options,
        formType: formData.type,
        formTitle: formData.title
      }, HEADERS);

      if ((res.status === 200 || res.data.status === 200) && res.data.data) {
        const improved = res.data.data;
        setFormData(prev => ({
          ...prev,
          fields: prev.fields.map((f, i) => i === index ? {
            ...f,
            label: improved.label || f.label,
            description: improved.description || f.description,
            options: improved.options || f.options
          } : f)
        }));
        Swal.fire('Improved!', 'Question enhanced with AI', 'success');
      }
    } catch (error) {
      Swal.fire('Error', 'Failed to improve question', 'error');
    } finally {
      setAiLoading(false);
    }
  };

  const saveForm = async () => {
    if (!formData.title.trim()) {
      return Swal.fire('Error', 'Please enter a form title', 'error');
    }
    if (formData.fields.length === 0) {
      return Swal.fire('Error', 'Please add at least one question', 'error');
    }

    setLoading(true);
    try {
      const payload = {
        title: formData.title,
        description: formData.description,
        type: formData.type,
        themeColor: formData.themeColor,
        fields: formData.fields,
        settings: formData.settings,
        isActive: formData.settings?.isActive !== false
      };

      const res = selectedForm?._id
        ? await axios.put(`/api/crm-forms/templates/${selectedForm._id}`, payload, HEADERS)
        : await axios.post('/api/crm-forms/templates', payload, HEADERS);

      if (res.status === 200 || res.data.status === 200) {
        Swal.fire('Success', `Form ${selectedForm?._id ? 'updated' : 'created'} successfully!`, 'success');
        setBuilderOpen(false);
        resetForm();
        fetchForms();
      }
    } catch (error) {
      Swal.fire('Error', 'Failed to save form', 'error');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData(DEFAULT_FORM);
    setSelectedForm(null);
  };

  const editForm = (form) => {
    setSelectedForm(form);
    setFormData({
      title: form.title || 'Untitled Form',
      description: form.description || '',
      type: form.type || 'SURVEY',
      themeColor: form.themeColor || '#673ab7',
      fields: form.fields || [],
      settings: { ...DEFAULT_FORM.settings, ...form.settings }
    });
    setBuilderOpen(true);
  };

  const deleteForm = async (formId) => {
    const result = await Swal.fire({
      title: 'Delete Form?',
      text: 'This will also delete all responses. This cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Delete'
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`/api/crm-forms/templates/${formId}`, HEADERS);
        Swal.fire('Deleted', 'Form has been deleted', 'success');
        fetchForms();
      } catch (error) {
        Swal.fire('Error', 'Failed to delete form', 'error');
      }
    }
  };

  const duplicateForm = async (form) => {
    setSelectedForm(null);
    setFormData({
      ...form,
      _id: undefined,
      title: `${form.title} (Copy)`,
      fields: form.fields?.map(f => ({ ...f, id: `field_${Date.now()}_${Math.random()}` })) || []
    });
    setBuilderOpen(true);
  };

  const copyFormLink = (formId) => {
    // Get database name from JWT token
    let db = '';
    try {
      const token = localStorage.getItem('userToken');
      if (token) {
        const decoded = jwtDecode(token);
        db = decoded.db || '';
      }
    } catch (e) {
      console.error('Error decoding token:', e);
    }
    
    const link = db 
      ? `${window.location.origin}/form/${db}/${formId}`
      : `${window.location.origin}/form/${formId}`;
    navigator.clipboard.writeText(link);
    Swal.fire({ title: 'Link Copied!', text: link, icon: 'success', timer: 2000 });
  };

  const openResponses = (form) => {
    setSelectedForm(form);
    fetchResponses(form._id);
    setResponsesOpen(true);
  };

  const openPreview = (form) => {
    setFormData({
      title: form.title,
      description: form.description,
      type: form.type,
      themeColor: form.themeColor || '#673ab7',
      fields: form.fields || [],
      settings: form.settings || {}
    });
    setPreviewOpen(true);
  };

  const openSendDialog = (form) => {
    setSelectedForm(form);
    setSendDialogOpen(true);
  };

  return (
    <Box sx={{ p: 3, bgcolor: '#f5f5f5', minHeight: 'calc(100vh - 200px)' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>Forms & Surveys</Typography>
          <Typography color="textSecondary">
            Create Google Forms-style surveys with AI assistance
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={aiLoading ? <CircularProgress size={20} /> : <AutoAwesome />}
            onClick={generateFormWithAI}
            disabled={aiLoading}
          >
            AI Generate
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => { resetForm(); setBuilderOpen(true); }}
          >
            Create Form
          </Button>
        </Box>
      </Box>

      {/* Forms Grid */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress />
        </Box>
      ) : forms.length === 0 ? (
        <Paper sx={{ p: 5, textAlign: 'center' }}>
          <Typography variant="h6" color="textSecondary" gutterBottom>
            No forms yet
          </Typography>
          <Typography color="textSecondary" sx={{ mb: 3 }}>
            Create your first form or generate one with AI
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button variant="outlined" startIcon={<AutoAwesome />} onClick={generateFormWithAI}>
              Generate with AI
            </Button>
            <Button variant="contained" startIcon={<Add />} onClick={() => { resetForm(); setBuilderOpen(true); }}>
              Create Form
            </Button>
          </Box>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {forms.map((form) => (
            <Grid item xs={12} sm={6} md={4} key={form._id}>
              <Card 
                sx={{ 
                  height: '100%', 
                  borderTop: `4px solid ${form.themeColor || '#673ab7'}`,
                  '&:hover': { boxShadow: 4 },
                  transition: 'box-shadow 0.2s'
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Chip 
                      label={form.type} 
                      size="small" 
                      color={FORM_TYPES.find(t => t.value === form.type)?.color || 'default'} 
                    />
                    <Chip 
                      label={form.isActive !== false ? 'Active' : 'Inactive'} 
                      size="small" 
                      color={form.isActive !== false ? 'success' : 'default'} 
                      variant="outlined" 
                    />
                  </Box>
                  
                  <Typography variant="h6" fontWeight={600} gutterBottom noWrap>
                    {form.title}
                  </Typography>
                  
                  <Typography 
                    variant="body2" 
                    color="textSecondary" 
                    sx={{ mb: 2, minHeight: 40, overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    {form.description || 'No description'}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="caption" color="textSecondary">
                      {form.fields?.length || 0} questions
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {new Date(form.createdAt).toLocaleDateString()}
                    </Typography>
                  </Box>

                  <Divider sx={{ mb: 2 }} />
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Box>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => editForm(form)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Preview">
                        <IconButton size="small" onClick={() => openPreview(form)}>
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Responses">
                        <IconButton size="small" onClick={() => openResponses(form)}>
                          <Analytics fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Send to Customers">
                        <IconButton size="small" onClick={() => openSendDialog(form)} color="primary">
                          <Send fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Box>
                      <Tooltip title="Duplicate">
                        <IconButton size="small" onClick={() => duplicateForm(form)}>
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Copy Link">
                        <IconButton size="small" onClick={() => copyFormLink(form._id)}>
                          <Share fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => deleteForm(form._id)} color="error">
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Form Builder Dialog */}
      <FormBuilder
        open={builderOpen}
        onClose={() => setBuilderOpen(false)}
        formData={formData}
        setFormData={setFormData}
        onSave={saveForm}
        loading={loading}
        aiLoading={aiLoading}
        onImproveQuestion={improveQuestionWithAI}
      />

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="h6">Form Preview</Typography>
          <IconButton onClick={() => setPreviewOpen(false)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ bgcolor: '#f0ebf8', p: 0 }}>
          <FormPreview formData={formData} />
        </DialogContent>
      </Dialog>

      {/* Responses Dialog */}
      <FormResponses
        open={responsesOpen}
        onClose={() => setResponsesOpen(false)}
        form={selectedForm}
        responses={responses}
        onRefresh={() => fetchResponses(selectedForm?._id)}
        loading={loading}
      />

      {/* Send Form Dialog */}
      <SendFormDialog
        open={sendDialogOpen}
        onClose={() => setSendDialogOpen(false)}
        form={selectedForm}
      />
    </Box>
  );
};

export default FormsManagement;
